import HttpRequest from "../../utilities/httpRequest";
import {
  addDept,
  getDeptById,
  getDepts,
  getDeptsForAdmin,
  getDeptsForUser,
  getDeptTypes,
  setAvailability,
  toggleActivation,
  updateDept,
} from "./DepartmentsActions";
import {
  hideLoader,
  showLoader,
  showSnackbar,
} from "../../utilities/UtilitiesActions";
import moment from "moment";

/**
 * function to get departments
 * @param orgId: orgId for api request
 * @param data: data for api request
 * @param isHideLoader: isHideLoader for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const getDeptsRequest = (orgId, data, isHideLoader) => {
  let offset = data.page * data.rowsPerPage;
  let limit = data.rowsPerPage;
  let searchData = data.searchData ? data.searchData : "";

  return (dispatch) => {
    if (!isHideLoader) {
      dispatch(showLoader());
    }

    const url = `department/org/${orgId}?offset=${offset}&limit=${limit}&searchData=${JSON.stringify(
      searchData
    )}`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(getDepts(res.data.data));

        if (!isHideLoader) {
          dispatch(hideLoader());
        }

        return Promise.resolve(res.data.data);
      },
      (error) => {
        if (!isHideLoader) {
          dispatch(hideLoader());
        }

        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get departments
 * @param id: id for api request
 * @param data: data for api request
 * @param isHideLoader: isHideLoader for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const getAvailabilityRequest = (id, isHideLoader) => {
  return (dispatch) => {
    // isHideLoader was accepted but ignored, so background callers flashed the
    // full-screen loader.
    if (!isHideLoader) {
      dispatch(showLoader());
    }

    const url = `org/getOne/workingHours?${id}`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(setAvailability(res.data.data));
        dispatch(hideLoader());
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get departments
 * @param data: data for api request
 * @param isHideLoader: isHideLoader for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const saveAvailabilityRequest = (data, isUpdate) => {
  return (dispatch) => {
    dispatch(showLoader());
    let url = `org/create/workingHours`;
    if (isUpdate) {
      url = `org/update/workingHours`;
    } else {
      url = `org/create/workingHours`;
    }
    return new HttpRequest(dispatch, url, isUpdate ? "put" : "post", data)
      .send()
      .then(
        (res) => {
          dispatch(setAvailability(res.data.data));
          dispatch(hideLoader());
          dispatch(
            showSnackbar(
              `Working hours ${isUpdate ? "updated" : "added"} successfully`,
              "success"
            )
          );
          return Promise.resolve(res.data.data);
        },
        (error) => {
          dispatch(hideLoader());
          if (error.response?.data?.error) {
            let errorMessage = error.response.data.error;

            const matches = errorMessage.match(/\d{13}/g);
            if (matches && matches.length >= 2) {
              const readableTimes = matches.map((ts) =>
                moment(Number(ts)).format(" HH:mm")
              );
              errorMessage = `Overlapping slots between ${readableTimes[0]} and ${readableTimes[1]}`;
            }

            dispatch(showSnackbar(errorMessage, "error"));
          }
          return Promise.reject(error);
        }
      );
  };
};

/**
 * function to get departments without params
 * @param orgId: orgId for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const getDeptsForOrgWithoutParamsRequest = (orgId) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `department/org/${orgId}`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getDepts(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

export const getAllDeptsRequest = () => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `departmentsByOrgs`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getDepts(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to add department
 * @param data: data for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const addDeptRequest = (data) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = "department";

    return new HttpRequest(dispatch, url, "post", data).send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(showSnackbar("Department created successfully", "success"));

        dispatch(addDept(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        if (error.response.data.error) {
          dispatch(showSnackbar(error.response.data.error, "error"));
        }
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get department by id
 * @param id: param for url
 * @returns {function(*=): Promise<unknown>}
 */
export const getDeptByIdRequest = (id) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `department/${id}`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getDeptById(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        if (error.response.data.error) {
          dispatch(showSnackbar(error.response.data.error, "error"));
        }
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to update department
 * @param id: id for api request
 * @param data: data for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const updateDeptRequest = (id, data) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `department/${id}`;

    return new HttpRequest(dispatch, url, "patch", data).send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(showSnackbar("Department updated successfully", "success"));

        dispatch(updateDept(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        if (error.response.data.error) {
          dispatch(showSnackbar(error.response.data.error, "error"));
        }
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get department types
 * @returns {function(*=): Promise<unknown>}
 */
export const getDeptTypesRequest = () => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = "department/types/all";

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getDeptTypes(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to toggle department activation
 * @param  {String, Object}     //deptId, data   //deptId, data for api request
 * @return {Promise}            //data           //data form api response
 */
/**
 * function to toggle department activation
 * @param deptId: deptId for api request
 * @param data: data for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const toggleActivationRequest = (deptId, data) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `department/${deptId}/activateOrDeactivateAccount`;

    return new HttpRequest(dispatch, url, "patch", data).send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(toggleActivation(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get departments for user
 * @param id: id for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const getDeptsForUserRequest = (id) => {
  return (dispatch) => {
    // dispatch(showLoader());
    const url = `users/singleUser/${id}`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        // dispatch(hideLoader());
        dispatch(getDeptsForUser(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        // dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

/**
 * @param data
 * @param isHideLoader
 * @returns {function(*=): Promise<unknown>}
 */
export const getDeptsForAdminRequest = (data, isHideLoader) => {
  let offset = data.page * data.rowsPerPage;
  let limit = data.rowsPerPage;
  let searchData = data.searchData ? data.searchData : "";

  return (dispatch) => {
    if (!isHideLoader) {
      dispatch(showLoader());
    }

    const url = `departmentAdmin?offset=${offset}&limit=${limit}&searchData=${JSON.stringify(
      searchData
    )}`;

    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        if (!isHideLoader) {
          dispatch(hideLoader());
        }

        dispatch(getDeptsForAdmin(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        if (!isHideLoader) {
          dispatch(hideLoader());
        }

        return Promise.reject(error);
      }
    );
  };
};

/**
 * function to get signed url
 * @param data: data for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const getSignedUrlRequest = (data) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `department/profilePic/signedURL`;
    return new HttpRequest(dispatch, url, "post", data)
      .send()
      .then((res) => {
        dispatch(hideLoader());
        return Promise.resolve(res.data.data);
      })
      .catch((error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      });
  };
};

/**
 * function to upload file on signed url
 * @param url: url for api request
 * @param file: data for api request
 * @returns {function(*=): Promise<unknown>}
 */
export const uploadToS3Request = (url, file) => {
  return (dispatch) => {
    dispatch(showLoader());
    return new HttpRequest(dispatch, url, "put", file)
      .s3Upload()
      .then((res) => {
        dispatch(hideLoader());
        return Promise.resolve(res.data);
      })
      .catch((error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      });
  };
};
